import { createClient } from '@libsql/client';
import { nanoid } from 'nanoid';

const db = createClient({
  url: import.meta.env.VITE_TURSO_DB_URL,
  authToken: import.meta.env.VITE_TURSO_AUTH_TOKEN
});

const DEFAULT_GODREJ_BARCA_CONTENT = {
  lastUpdated: new Date().toISOString(),
  pageTitle: "Barca at Godrej MSR City | Antacres",
  heroSection: {
    title: "Postcards from Paradise",
    subtitle: "GODREJ MSR CITY",
    tagline: "Mediterranean Living in North Bangalore",
    backgroundImage: "https://gharnmakaan.com/wp-content/uploads/2024/10/Ace-Hanei-Floor-Plan-Price-Location-Brochure.jpg",
  },
  aboutProject: {
    heading: "Bengaluru's Seen It All. But This? This Is Something Else.",
    text: [
      "I've lived in Bengaluru long enough to know a good thing when I see it. The city's energy, its ever-evolving skyline, the charm of its old neighbourhoods merging effortlessly with the new, it's a place that never stops surprising you. But every now and then, something comes along that truly stands out. Godrej Properties. It's not just good, it's legendary.",
      "A name I've always known and come to trust. Godrej Properties, with 128 years of shaping industries, building trust, and redefining excellence. It's more than just a legacy, it's a benchmark. And in a city that's always reaching for the future, Godrej isn't just keeping up. It's been leading the way since 1897. From iconic addresses to communities built for a life well-lived, they don't just construct buildings, they craft experiences. And looking at their journey, one thing is clear: when you build with passion and precision, recognition follows.",
      "From the bustling streets of Bengaluru to pockets across India, Godrej Properties has been shaping skylines and lifestyles with trust and excellence. With 102 projects spanning 222.4 million square feet across 10 cities, it's no wonder they've earned the title of India's No.1 Real Estate Developer. And with a record-breaking booking value of over 22,527 Crores in FY 24, the journey is only getting grander, just like the timeless allure of the Mediterranean way of life."
    ],
    image: "https://img.freepik.com/premium-photo/balcony-with-sunset-view-couch_531533-2936.jpg"
  },
  
  location: {
    heading: "North Bengaluru. Where The Future Takes Flight.",
    highlights: [
      {
        title: "Rocketing into the Future from Namma Bengaluru.",
        points: ["Tech Cloud: 3 KMS", "Aero City: 6 KMS", "Signature Business Park: 8 KMS", "13 Degree Mall: 2 KMS", "JW Marriot International: 2 KMS"],
        text : ["Namma Bengaluru, the vibrant heart of innovation, is where dreams take flight and ideas touch the stars. From bustling tech hubs to pioneering space research centers, the city thrives on a spirit of discovery and progress. Here, every breakthrough is a step toward redefining possibilities, with innovations born in Bengaluru resonating across the globe. It’s a place where boundaries are meant to be crossed, and the future is shaped with bold vision. From Namma Bengaluru, we are not just reaching the future — we are rocketing into it."],
        image: "https://prd-sc102-cdn.rtx.com/-/media/rtx/news-images/2024/07/singapore.jpg?rev=a8c263561eb34c72bae0075daea533c3"
      },
      {
        title: "Fast Lanes & Slow Living, Best of Both Worlds.",
        points: ["Kempegowda International Airport: 9 KMS", "NH44 Bellary Road: 2. KMS", "Upcoming Dodajalla Metro Station: 4 KMS", "Upcoming Peripheral Ring Road: 12 KMS", "Satellite Town Ring Road: 15 KMS"],
        text: ["In North Bengaluru, the NH44, Peripheral Ring Road and Doddajala Metro bring the city closer, but life here is all about the easy-going Mediterranean way, where every ride feels like a scenic escape, and every road leads to something special."],
        image: "https://i.pinimg.com/originals/88/b9/80/88b9808bbccc62da91b30b5c89ea7f91.jpg"
      },
      {
        title: "Bengaluru Moves Fast, And So Does The IT Sector.",
        points: ["Karle Town SEZ: 23 KMS", "IFCI Financial City: 8 KMS", "Ecopolis Hinduja: 12 KMS", "Bagmane Sierra Business District: 17 KMS", "Horizon (Amazon Office): 11 KMS", "KIADB Hardware Tech Park: 8 KMS", "KIADB Aerospace Tech Park: 9 KMS", "Manyata Tech Park: 21 KMS", "L&T Tech Park: 18 KMS"],
        text:["North Bengaluru is gearing up to be the city's next big business hub. Think buzzing tech parks, thriving industries and endless opportunities. The future of work and innovation? It's all happening right here. With leading tech parks nearby, I'm always in the right place at the right time. Shorter commutes, better opportunities, this is where growth happens"],
        image: "https://meenakshigroup.com/images/rock-overview.png"
      },
      {
        title: "Good Schools Make Great Neighbourhoods.",
        points: ["Delhi Public School Bangalore North: 15 KMS", "Canadian International School: 14 KMS", "Ryan International School: 21 KMS", "Jawahar Navodaya Vidyalaya: 9 KMS", "Stone Hill International School: 10 KMS", "Sir M. Visvesvaraya Institute of Technology: 10 KMS", "Reva University: 14 KMS"],
        text:["Lucky for me, some of the best ones are just around the corner. From international schools to top universities, quality education is never too far, just the way it should be."],
        image: "https://img.freepik.com/premium-photo/parent-pupil-going-school_566707-3966.jpg"
      },
      {
        title: "Peace of Mind Comes From Knowing Help Is Always Close.",
        points: ["Relive Hospital: 11 KMS", "Manipal Hospital: 19 KMS", "Aster CMI Hospital: 21 KMS", "Baptist Hospital: 21 KMS", "Cyte Care Hospital: 11 KMS", "Sparsh Hospital: 14 KMS"],
        text:["With top hospitals just a short drive away, I know I'm covered no matter what. World-class care, expert doctors, and emergency services, because good health deserves great infrastructure."],
        image: "https://img.freepik.com/premium-photo/doctor-patient-hd-8k-wallpaper-stock-photographic_1064748-31349.jpg"
      },
      {
        title: "Weekends Here Are A Dream.",
        points: ["Elements Mall: 22 KMS", "Taj Airport Hotel: 7 KMS", "Mall of Asia: 18 KMS", "Galleria Mall: 17 KMS", "Bharthiya Mall of Bengaluru: 16 KMS"],
        text:["Luxury hotels for a quick getaway, malls packed with my favourite brands and cafes that make every meal feel special, whether I'm shopping, dining or just unwinding, there's always something exciting around the corner."],
        image: "https://img.freepik.com/premium-photo/exterior-stylish-cafe-8k-resolution-fine-hype_943281-45218.jpg"
      }
    ]
  },

mediterraneanWay: {
    heading: "Presenting The Mediterranean Way.",
    text: [
      "Woke up in Bengaluru, but it feels like the Mediterranean! Just booked my ticket, but I’m still in Bengaluru. But the moment I step outside, it feels like I’ve wandered into a European daydream. Morning walks feel like a stroll through Milan, afternoons are straight out of a Barcelona café. And evenings? They’re a mix of laughter, music and conversations that flow as freely as the city itself. Sunlit spaces, breezy balconies, and a vibe that’s straight out of Europe. If this is home, I’m never leaving!",
      "Woke up to sun-drenched terraces and freshly-brewed coffee, just the way I like them. By afternoon, the pool calls and I soak in the warmth, letting time slip away. Evenings? They’re a mix of laughter, music and conversations that flow as freely as the city itself. Every day feels like a stroll through La Rambla, full of life and always unforgettable."
    ],
    image: "https://i.pinimg.com/736x/9c/c9/4f/9cc94f7e6c03b65e199304ecb7277cbb.jpg"
  },

  amenities: {
    heading: "Greetings From My New Favourite Spot!",
    description: "This clubhouse isn’t just grand, it’s a slice of the Mediterranean spread over 1.5 Lakh Sq. Ft., right in Bengaluru.",
    amenityList: [
      {
        heading: "Largest Gym",
        text: "Dumbbells clinking, treadmills humming and the kind of space that makes every workout feel effortless. This gym isn’t just big, it’s built for champions. Sweat. Recharge. Repeat. See you here?",
        image: "https://www.fitnessfirst.co.uk/media/xicpdmkj/ff-stalbans-aug2024-160.jpg?width=1920&height=1080&format=webp&quality=80&v=1daecc9f31c9810"
      },
      {
        heading: "Biggest WFH Facility",
        text: "10,000 Sq.Ft. Co-working space and Business Centre.",
        image: "https://images.steelcase.com/image/upload/v1539114239/www.steelcase.com/2018/10/09/18-0110817-1.jpg"
      },
      {
        heading: "Kids' Arena",
        text: "3,500 Sq.Ft. Kids' Arena.",
        image: "https://dynamic-media-cdn.tripadvisor.com/media/photo-o/1a/cc/9d/83/tummel-dschungel-indoorspielpl.jpg?w=1200&h=1200&s=1"
      },
      {
        heading: "Skating Rink",
        text: "This isn’t just a skating rink, it’s a rhythm-filled escape. With music in the air and the smooth track beneath my feet, every ride feels like a dance under the Mediterranean sun.",
        image: "https://tothotornot.com/wp-content/uploads/2022/08/IceHQ-1536x836.jpg"
      },
      {
        heading: "Lap Pool",
        text: "Making waves, one lap at a time! Slip into the water, take a breath, and let everything fade away. Whether for a workout or peace, the pool never disappoints.",
        image: "https://th.bing.com/th/id/R.97ee0a978416eb0fbc179f56898d7cec?rik=6Nb1xxVk6T%2bIGg&riu=http%3a%2f%2fst.hzcdn.com%2ffimgs%2f84f193ff00fdcda2_9674-w500-h400-b0-p0--contemporary-pool.jpg&ehk=7opm85ggKVx8nh3g1npwj1saS%2f2abwPwIwBYGqNUpNw%3d&risl=&pid=ImgRaw&r=0"
      },
      {
        heading: "Gaudi Leisure Cafe",
        text: "Where breaks are brewed to perfection. Post-game or mid-day, the café is the perfect pause. A sip of coffee, a bite of something delicious, and great conversations all around.",
        image: "https://media-cdn.tripadvisor.com/media/photo-s/2c/ad/fc/21/our-cozy-cafe-is-perfect.jpg"
      }
    ]
  },
  sitePlan: {
    heading: "Master Layout Plan",
    description :" Explore a comprehensive range of amenities designed for a holistic living experience. " ,
    image: "https://tricohomes.com/wp-content/uploads/2024/05/ChelseaCourt-SiteMap.webp",
   
  },
  aboutDeveloper: {
    heading: "About the Developer",
    subheading: "Godrej Properties",
    description: "Godrej Properties brings the Godrej Group philosophy of innovation, sustainability and excellence to the real estate industry. Each Godrej Properties development combines a 127-year legacy of excellence and trust with a commitment to cutting-edge design and technology. Godrej Properties is currently developing residential, commercial and township projects spread across approximately 18.66 million square meters (200 million square feet) in 12 cities.",
    record: "Record-breaking booking value of over 22,527 Crores in FY 24!",
    logo: "https://searchlogovector.com/wp-content/uploads/2018/05/godrej-properties-logo-vector.png"
  },
  
  prebookSection: {
    heading: "Pre-Book Your Dream Home Now!",
    tagline: "Don't miss the opportunity to live the Mediterranean way in North Bengaluru."
  }

};

const ensureTableExists = async () => {
  try {
    await db.execute(`
      CREATE TABLE IF NOT EXISTS godrej_content (
        id TEXT PRIMARY KEY,
        content_data TEXT NOT NULL,
        updated_at TEXT NOT NULL
      );
    `);
    console.log('godrej_content table ensured.');
  } catch (error) {
    console.error("Error ensuring godrej_content table exists:", error);
    throw new Error('Failed to create godrej_content table.');
  }
};

async function getGodrejContent() {
  await ensureTableExists();
  try {
    const result = await db.execute({
      sql: `SELECT content_data, updated_at FROM godrej_content WHERE id = 'godrej-barca-msr';`,
      args: [],
    });
    
    if (result.rows.length > 0) {
      const content = JSON.parse(result.rows[0].content_data);
      content.lastUpdated = result.rows[0].updated_at;
      return content;
    } else {
      console.log('No content found for Godrej Barca MSR, returning default and saving it.');
      await createOrUpdateGodrejContent(DEFAULT_GODREJ_BARCA_CONTENT);
      return DEFAULT_GODREJ_BARCA_CONTENT;
    }
  } catch (error) {
    console.error("Error fetching Godrej Barca MSR content:", error);
    throw new Error('Could not fetch Godrej Barca MSR content from database.');
  }
}

async function createOrUpdateGodrejContent(content) {
  await ensureTableExists();
  const now = new Date().toISOString();
  try {
    const serializedContent = JSON.stringify({ ...content, lastUpdated: now });
    await db.execute({
      sql: `
        INSERT INTO godrej_content (id, content_data, updated_at) VALUES (?, ?, ?)
        ON CONFLICT(id) DO UPDATE SET content_data = ?, updated_at = ?;
      `,
      args: [
        'godrej-barca-msr',
        serializedContent,
        now,
        serializedContent,
        now
      ],
    });
    console.log('Godrej Barca MSR content created or updated successfully.');
  } catch (error) {
    console.error('Error creating/updating Godrej Barca MSR content:', error);
    throw new Error('Could not save Godrej Barca MSR content to database.');
  }
}

export const godrejBarcaMsrService = {
  getGodrejContent,
  createOrUpdateGodrejContent
};